/**
 * Historique d'activité de l'évaluation + chat persona + presence.
 *
 * @remarks
 * `personaAvailability` n'est PAS persisté — c'est un état runtime alimenté
 * par SSE/WS (en attendant l'intégration, on simule via `setPersonaAvailability`).
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { uid } from '../lib/status-helpers';
import type { ChatMessage, HistoryEntry, PersonaAvailability } from '../types';

interface HistoryState {
  historyEntries: Record<string, HistoryEntry[]>;
  chatMessages: Record<string, ChatMessage[]>;
  personaAvailability: Record<string, PersonaAvailability>;

  addHistoryEntry: (evaluationId: string, entry: Omit<HistoryEntry, 'id' | 'timestamp'>) => void;
  addChatMessage: (threadId: string, message: Omit<ChatMessage, 'id' | 'sentAt'>) => void;
  setPersonaAvailability: (personaId: string, availability: PersonaAvailability) => void;
}

// Seed mock pour evaluation-1-2 — démo des types d'événements (newest first).
const _n = Date.now();
const _m = 60_000;
const SEED_HISTORY: Record<string, HistoryEntry[]> = {
  'evaluation-1-2': [
    {
      id: 'hist-e12-14',
      type: 'working-doc-edit',
      payload: { name: 'Checklist composants' },
      timestamp: _n - 1 * _m,
    },
    {
      id: 'hist-e12-13',
      type: 'resource-download',
      payload: { name: 'Design System Assets' },
      timestamp: _n - 3 * _m,
    },
    {
      id: 'hist-e12-12',
      type: 'working-doc-create',
      payload: { name: 'Notes — Archi composants' },
      timestamp: _n - 6 * _m,
    },
    {
      id: 'hist-e12-11',
      type: 'document-open',
      payload: { name: 'Guide Playwright E2E' },
      timestamp: _n - 8 * _m,
    },
    {
      id: 'hist-e12-10',
      type: 'working-doc-edit',
      payload: { name: 'Questions — Specs UI' },
      timestamp: _n - 10 * _m,
    },
    {
      id: 'hist-e12-09',
      type: 'chat-message',
      payload: { name: 'Alex Martin' },
      timestamp: _n - 13 * _m,
    },
    {
      id: 'hist-e12-08',
      type: 'working-doc-edit',
      payload: { name: 'Checklist composants' },
      timestamp: _n - 16 * _m,
    },
    {
      id: 'hist-e12-07',
      type: 'working-doc-create',
      payload: { name: 'Questions — Specs UI' },
      timestamp: _n - 18 * _m,
    },
    {
      id: 'hist-e12-06',
      type: 'working-doc-create',
      payload: { name: 'Checklist composants' },
      timestamp: _n - 20 * _m,
    },
    {
      id: 'hist-e12-05',
      type: 'document-open',
      payload: { name: 'Patterns de Tests React' },
      timestamp: _n - 22 * _m,
    },
    {
      id: 'hist-e12-04',
      type: 'resource-download',
      payload: { name: 'Component Mock Data' },
      timestamp: _n - 24 * _m,
    },
    {
      id: 'hist-e12-03',
      type: 'resource-download',
      payload: { name: 'Design System Starter' },
      timestamp: _n - 26 * _m,
    },
    {
      id: 'hist-e12-02',
      type: 'document-open',
      payload: { name: 'Stratégie de Tests' },
      timestamp: _n - 28 * _m,
    },
    {
      id: 'hist-e12-01',
      type: 'evaluation-start',
      payload: { name: 'Développement des composants' },
      timestamp: _n - 30 * _m,
    },
  ],
};

export const useHistoryStore = create<HistoryState>()(
  devtools(
    persist(
      (set) => ({
        historyEntries: SEED_HISTORY,
        chatMessages: {},
        personaAvailability: {},

        addHistoryEntry: (evaluationId, entry) => {
          const id = uid('hist');
          set((state) => ({
            historyEntries: {
              ...state.historyEntries,
              [evaluationId]: [
                { ...entry, id, timestamp: Date.now() },
                ...(state.historyEntries[evaluationId] ?? []),
              ],
            },
          }));
        },

        addChatMessage: (threadId, message) => {
          const id = uid('msg');
          set((state) => ({
            chatMessages: {
              ...state.chatMessages,
              [threadId]: [
                ...(state.chatMessages[threadId] ?? []),
                { ...message, id, sentAt: Date.now() },
              ],
            },
          }));
        },

        setPersonaAvailability: (personaId, availability) => {
          set((state) => ({
            personaAvailability: {
              ...state.personaAvailability,
              [personaId]: availability,
            },
          }));
        },
      }),
      {
        name: 'saas-front-v2:tech-tests-history',
        version: 1,
        partialize: (state) => ({
          historyEntries: state.historyEntries,
          chatMessages: state.chatMessages,
          // personaAvailability pas persisté — runtime SSE/WS
        }),
        /**
         * Ce store est en `version: 1` depuis sa création : aucune version
         * antérieure n'existe. `migrate` n'est donc atteint que sur un payload
         * d'origine inconnue, dont la forme n'est pas garantie — on repart d'un
         * historique **vide**.
         *
         * Le fallback ne doit surtout pas valoir `SEED_HISTORY` : l'utilisateur
         * verrait un historique fabriqué (« Design System Starter », « Stratégie
         * de Tests »…) présenté comme le sien, et zustand réécrit le résultat de
         * la migration dans le storage.
         *
         * ⚠️ Au prochain bump, ajouter la transformation **avant** ce retour et
         * comparer avec `<` : `if (oldVersion < 2) { …adapter… }`. Ne jamais
         * tester `oldVersion === <version courante>` — zustand n'appelle
         * `migrate` que si les versions diffèrent, la branche serait morte.
         * Motif en cascade de référence : `use-layout-store`. (SAAS-903)
         */
        migrate: () => ({ historyEntries: {}, chatMessages: {} }),
      },
    ),
    { name: 'history', enabled: import.meta.env.DEV },
  ),
);
